import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

import { IFakturowniaInvoiceListRow } from 'core/hooks';
import { PATHS } from 'router/config';
import {
  InvoiceDirectionPill,
  InvoiceKindPill,
  InvoiceStatusPill,
  MatchStatusDot,
  MoneyCell,
  OverduePill,
} from 'molecules';

import * as S from './FakturowniaInvoices.styled';

interface IInvoicesTableProps {
  rows: IFakturowniaInvoiceListRow[];
  /**
   * Optional inline «Привязать к взносу» callback — surfaced on linkable
   * rows that have no `contribution` yet (spec 0004). Routed to
   * `LinkInvoicePaymentPopup` (contribution flow by default).
   */
  onLinkRow?: (row: IFakturowniaInvoiceListRow) => void;
}

const overdueDays = (row: IFakturowniaInvoiceListRow): number => {
  if (row.status?.toUpperCase() === 'PAID') return 0;
  if (!row.paymentTo) return 0;
  const diff = moment().startOf('day').diff(moment(row.paymentTo), 'days');
  return diff > 0 ? diff : 0;
};

/** Render the Fakturownia invoice list — columns straight from TZ §3.2. */
export const InvoicesTable = memo(
  ({ rows, onLinkRow }: IInvoicesTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const openDetail = useCallback(
      (id: string) => {
        history.push(PATHS.financesInvoice.replace(':id', id));
      },
      [history],
    );

    return (
      <S.Table>
        <thead>
          <tr>
            <th>{t('pages.fakturownia.invoices.columns.number')}</th>
            <th>{t('pages.fakturownia.invoices.columns.direction')}</th>
            <th>{t('pages.fakturownia.invoices.columns.kind')}</th>
            <th>{t('pages.fakturownia.invoices.columns.status')}</th>
            <th>{t('pages.fakturownia.invoices.columns.date')}</th>
            <th>{t('pages.fakturownia.invoices.columns.counterparty')}</th>
            <th>{t('pages.fakturownia.invoices.columns.group')}</th>
            <th style={{ textAlign: 'right' }}>
              {t('pages.fakturownia.invoices.columns.gross')}
            </th>
            <th style={{ textAlign: 'right' }}>
              {t('pages.fakturownia.invoices.columns.outstanding')}
            </th>
            <th>{t('pages.fakturownia.invoices.columns.match')}</th>
            <th>{t('pages.fakturownia.invoices.columns.link')}</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const overdue = overdueDays(row);
            const counterparty =
              row.linkedStudent?.user?.email ||
              row.buyerEmail ||
              row.buyerName ||
              '—';
            const isCorrection = Boolean(row.isCorrection);
            // Phase 7: refund/correction rows take the same muted background
            // pattern as ExpensesTable so the financier scans the list and
            // immediately spots which lines are reversals.
            const rowStyle = isCorrection
              ? { background: '#fafafa' }
              : undefined;
            return (
              <tr
                key={row.id}
                onClick={() => openDetail(row.id)}
                style={rowStyle}
              >
                <S.NameCell>
                  {row.number}
                  {isCorrection && row.parentInvoice ? (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 400,
                        marginTop: 2,
                      }}
                    >
                      {t('pages.fakturownia.invoices.correctionTo', {
                        number: row.parentInvoice.number,
                      })}
                    </div>
                  ) : null}
                  {/* Phase 8: symmetric badge — when the toggle is OFF and
                      a corrected parent shows up, mark it visibly so the
                      financier knows the gross figure isn't the final word. */}
                  {!isCorrection &&
                  row.corrections &&
                  row.corrections.length > 0 ? (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#1d4ed8',
                        fontWeight: 600,
                        marginTop: 2,
                      }}
                    >
                      ↩ {t('pages.fakturownia.invoices.hasCorrectionBadge')}
                    </div>
                  ) : null}
                  {/* Phase 12: explain why this row's amount isn't in the
                      KPI sums — estimate / proforma documents are excluded
                      from accounting totals because they're framing, not
                      money-in-the-bank events. */}
                  {row.isQuotation ? (
                    <div
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        fontStyle: 'italic',
                        marginTop: 2,
                      }}
                    >
                      {t('pages.fakturownia.invoices.notInKpiHint')}
                    </div>
                  ) : null}
                </S.NameCell>
                <S.TagCell>
                  <InvoiceDirectionPill direction={row.direction} />
                </S.TagCell>
                <S.TagCell>
                  <InvoiceKindPill
                    kind={row.kind}
                    isCorrection={row.isCorrection}
                    isAdvance={row.isAdvance}
                    isFinal={row.isFinal}
                    isEstimate={row.isEstimate}
                    isQuotation={row.isQuotation}
                  />
                </S.TagCell>
                <S.TagCell>
                  <InvoiceStatusPill status={row.status} />
                  {overdue > 0 && (
                    <>
                      {' '}
                      <OverduePill days={overdue} />
                    </>
                  )}
                </S.TagCell>
                <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  {row.issueDate
                    ? moment(row.issueDate).format('DD.MM.YYYY')
                    : '—'}
                </td>
                <td>{counterparty}</td>
                <td>{row.linkedGroup?.name || '—'}</td>
                <td>
                  <MoneyCell
                    amount={row.priceGross}
                    currency={row.currency}
                    negative={isCorrection}
                  />
                </td>
                <td>
                  <MoneyCell
                    amount={row.outstanding}
                    currency={row.currency}
                    highlightOutstanding
                  />
                </td>
                <S.TagCell>
                  <MatchStatusDot matchStatus={row.matchStatus} />
                </S.TagCell>
                {/* Spec 0004 «Привязка»: green = invoice pinned to a взнос
                    (date · amount shown), orange dot = not linked yet, red
                    badge = hidden-Payment auto-provision blocked. */}
                <S.TagCell>
                  {row.contribution ? (
                    <span
                      title={t(
                        'pages.fakturownia.invoices.linkedContributionTitle',
                      )}
                      style={{
                        color: '#16a34a',
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ●{' '}
                      {row.contribution.date
                        ? moment(row.contribution.date).format('DD.MM.YYYY')
                        : '—'}
                      {row.contribution.amountForeign != null
                        ? ` · ${row.contribution.amountForeign}`
                        : ''}
                    </span>
                  ) : (
                    <span
                      title={t(
                        'pages.fakturownia.invoices.noContributionTitle',
                      )}
                      style={{ color: '#f59e0b', fontSize: 12 }}
                    >
                      ●
                    </span>
                  )}
                  {row.provisionBlockedReason ? (
                    <span
                      title={t(
                        `pages.fakturownia.invoices.provisionBlocked.${row.provisionBlockedReason}`,
                        { defaultValue: row.provisionBlockedReason },
                      )}
                      style={{
                        marginLeft: 6,
                        display: 'inline-block',
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: '#fee2e2',
                        color: '#991b1b',
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      !
                    </span>
                  ) : null}
                </S.TagCell>
                <S.RowActionsCell onClick={(e) => e.stopPropagation()}>
                  {/* Spec 0004: linkable rows without a взнос get the inline
                      «Привязать к взносу» action. */}
                  {onLinkRow &&
                  !row.contribution &&
                  !isCorrection &&
                  !row.isQuotation ? (
                    <S.InlineButton
                      type="button"
                      onClick={() => onLinkRow(row)}
                    >
                      {t('pages.fakturownia.invoices.linkContribution')}
                    </S.InlineButton>
                  ) : null}
                </S.RowActionsCell>
              </tr>
            );
          })}
        </tbody>
      </S.Table>
    );
  },
);
